// WCAG 1.3.2 (Meaningful Sequence): uiv's popup mixin defaults
// `appendTo: 'body'`, moving every popover/tooltip out of reading order.
// This re-hosts the shown popup as the trigger's next sibling and switches
// it from uiv's document-coordinate `absolute` to a viewport-coordinate
// `fixed` position kept in sync on scroll, so it still paints where uiv put
// it without living at the end of <body>.
//
// Declines to relocate when an ancestor establishes a CSS containing block
// for `position: fixed` (transform/filter/perspective/backdrop-filter always;
// will-change/contain/content-visibility only for specific values) — a fixed
// descendant of such an ancestor paints relative to it, not the viewport, so
// relocating there would move where the popup paints.

const SIMPLE_CONTAINING_BLOCK_PROPERTIES = [
  'transform',
  'filter',
  'perspective',
  'backdropFilter',
]

// Only these `will-change` values name a property that itself establishes a
// containing block — `will-change: opacity` does not.
const WILL_CHANGE_CONTAINING_BLOCK_VALUES = new Set([
  'transform',
  'filter',
  'perspective',
  'backdrop-filter',
])

// Only these `contain` values establish a containing block — `size`/`style`
// alone do not.
const CONTAIN_CONTAINING_BLOCK_VALUES = new Set(['layout', 'paint', 'strict', 'content'])

function establishesContainingBlock(computedStyle) {
  if (
    SIMPLE_CONTAINING_BLOCK_PROPERTIES.some((prop) => {
      const value = computedStyle[prop]
      return value && value !== 'none'
    })
  ) {
    return true
  }

  const willChange = computedStyle.willChange
  if (
    willChange &&
    willChange !== 'none' &&
    willChange !== 'auto' &&
    willChange.split(',').some((prop) => WILL_CHANGE_CONTAINING_BLOCK_VALUES.has(prop.trim()))
  ) {
    return true
  }

  const contain = computedStyle.contain
  if (
    contain &&
    contain !== 'none' &&
    contain.split(/\s+/).some((value) => CONTAIN_CONTAINING_BLOCK_VALUES.has(value))
  ) {
    return true
  }

  const contentVisibility = computedStyle.contentVisibility
  if (contentVisibility && contentVisibility !== 'visible') {
    return true
  }

  return false
}

function hasContainingBlockAncestor(el) {
  for (let node = el.parentElement; node; node = node.parentElement) {
    if (establishesContainingBlock(getComputedStyle(node))) {
      return true
    }
  }
  return false
}

function parsePx(value) {
  const n = parseFloat(value)
  return Number.isNaN(n) ? 0 : n
}

// Moves `popup` to be `trigger`'s next sibling and repositions it as a
// viewport-fixed element, preserving where uiv already placed it. Returns a
// `release()` to call on hide/destroy, or `null` if relocation was declined
// (popup is left exactly where uiv put it).
export function placePopupBesideTrigger(popup, trigger) {
  if (!popup || !trigger || !trigger.parentNode) return null
  if (hasContainingBlockAncestor(trigger)) return null

  // uiv's resetPosition() already set these as document-absolute offsets
  // before emitting 'show'; capture them before moving anything.
  const documentTop = parsePx(popup.style.top)
  const documentLeft = parsePx(popup.style.left)

  // Track the trigger's own rect delta rather than window.scrollX/Y alone —
  // a nested `overflow` ancestor can scroll (moving the trigger) without the
  // window scrolling at all, and getBoundingClientRect() reflects both.
  const initialTriggerRect = trigger.getBoundingClientRect()
  const initialViewportTop = documentTop - window.scrollY
  const initialViewportLeft = documentLeft - window.scrollX

  trigger.parentNode.insertBefore(popup, trigger.nextSibling)
  popup.style.position = 'fixed'

  const reposition = () => {
    const triggerRect = trigger.getBoundingClientRect()
    popup.style.top = `${initialViewportTop + triggerRect.top - initialTriggerRect.top}px`
    popup.style.left = `${initialViewportLeft + triggerRect.left - initialTriggerRect.left}px`
  }
  reposition()

  // Capture phase: a nested scroll container's scroll doesn't bubble.
  window.addEventListener('scroll', reposition, true)

  return () => {
    window.removeEventListener('scroll', reposition, true)
  }
}
